import { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, Circle, Marker, Popup, TileLayer, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin, Navigation, Play, Plus, Save, Square, Trash2 } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const HAJIPUR = [25.710566, 85.222825];

const pin = (colour) =>
  L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;border-radius:9999px;background:${colour};box-shadow:0 0 0 4px rgba(0,0,0,.15)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

const FRESH_COLOURS = { live: "#10b981", delayed: "#f59e0b", offline: "#ef4444", no_data: "#94a3b8" };

const freshBadge = (row) => (
  <Badge
    className="text-[10px]"
    variant={row.freshness === "live" ? "default" : row.freshness === "delayed" ? "outline" : "destructive"}
    data-testid={`tracking-freshness-${row.id}`}
  >
    {row.label}
  </Badge>
);

const minutesAgo = (seconds) =>
  seconds == null ? "never" : seconds < 60 ? `${seconds}s ago` : `${Math.round(seconds / 60)} min ago`;

const LiveMap = ({ rows, locations, trail }) => {
  const centre = rows.find((r) => r.last_location)
    ? [rows.find((r) => r.last_location).last_location.lat, rows.find((r) => r.last_location).last_location.lng]
    : locations[0]
      ? [locations[0].lat, locations[0].lng]
      : HAJIPUR;
  return (
    <div className="surface overflow-hidden" data-testid="tracking-map">
      <MapContainer center={centre} zoom={13} style={{ height: 420, width: "100%" }}>
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {locations.filter((l) => l.active).map((l) => (
          <Circle key={l.id} center={[l.lat, l.lng]} radius={l.radius_m}
            pathOptions={{ color: "#12513B", fillColor: "#12513B", fillOpacity: 0.12 }}>
            <Popup>{l.name} — {l.radius_m} m geofence</Popup>
          </Circle>
        ))}
        {rows.filter((r) => r.last_location).map((r) => (
          <Marker key={r.id} position={[r.last_location.lat, r.last_location.lng]}
            icon={pin(FRESH_COLOURS[r.freshness] || "#94a3b8")}>
            <Popup>
              <b>{r.user_name}</b><br />
              {r.label} • updated {minutesAgo(r.age_seconds)}<br />
              accuracy ±{r.last_location.accuracy ?? "?"} m
            </Popup>
          </Marker>
        ))}
        {trail.length > 1 && (
          <Polyline positions={trail.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#C09A3E" }} />
        )}
      </MapContainer>
    </div>
  );
};

const Geofences = ({ locations, employees, reload }) => {
  const [form, setForm] = useState({
    name: "", lat: HAJIPUR[0], lng: HAJIPUR[1], radius_m: 150, kind: "Factory",
    address: "", active: true, employees: [],
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the location a name");
    setSaving(true);
    try {
      await api.post("/tracking/locations", { ...form, lat: Number(form.lat), lng: Number(form.lng), radius_m: Number(form.radius_m) });
      toast.success("Work location saved");
      setForm({ ...form, name: "", address: "", employees: [] });
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const useMyPosition = () =>
    navigator.geolocation?.getCurrentPosition(
      (p) => setForm((f) => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude })),
      () => toast.error("Location unavailable on this device")
    );

  return (
    <div className="space-y-4">
      <div className="surface space-y-3 p-4" data-testid="geofence-form">
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Location name">
            <Input data-testid="geofence-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Type">
            <Select value={form.kind} onValueChange={(kind) => setForm({ ...form, kind })}>
              <SelectTrigger data-testid="geofence-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Factory", "Office", "Project Site", "Installation Site", "Approved Field Location"].map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Latitude">
            <Input data-testid="geofence-lat" value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })} />
          </Field>
          <Field label="Longitude">
            <Input data-testid="geofence-lng" value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })} />
          </Field>
          <Field label="Allowed radius (metres)">
            <Input type="number" data-testid="geofence-radius" value={form.radius_m}
              onChange={(e) => setForm({ ...form, radius_m: e.target.value })} />
          </Field>
          <Field label="Address / landmark" className="sm:col-span-2">
            <Input data-testid="geofence-address" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="Active">
            <div className="pt-2">
              <Switch checked={form.active} data-testid="geofence-active"
                onCheckedChange={(active) => setForm({ ...form, active })} />
            </div>
          </Field>
        </div>
        <div>
          <div className="overline mb-1">Assigned employees (leave empty = everyone)</div>
          <div className="flex flex-wrap gap-1.5">
            {employees.map((e) => (
              <button key={e.id} type="button" data-testid={`geofence-employee-${e.id}`}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    employees: f.employees.includes(e.id)
                      ? f.employees.filter((x) => x !== e.id)
                      : [...f.employees, e.id],
                  }))
                }
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  form.employees.includes(e.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:bg-secondary"
                }`}>
                {e.name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={saving} className="gap-1.5" data-testid="geofence-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add work location
          </Button>
          <Button size="sm" variant="outline" onClick={useMyPosition} data-testid="geofence-use-my-location">
            Use my current position
          </Button>
        </div>
      </div>

      {locations.length === 0 ? (
        <Empty title="No work locations yet" hint="Add the factory, office or a project site to enable geofenced attendance" />
      ) : (
        <div className="space-y-2" data-testid="geofence-list">
          {locations.map((l) => (
            <div key={l.id} className="surface flex flex-wrap items-center justify-between gap-3 p-4"
              data-testid={`geofence-row-${l.id}`}>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MapPin className="h-4 w-4 text-primary" /> {l.name}
                  <Badge variant="outline" className="text-[10px]">{l.kind}</Badge>
                  {!l.active && <Badge variant="destructive" className="text-[10px]">inactive</Badge>}
                </div>
                <p className="font-mono-num text-xs text-muted-foreground">
                  {Number(l.lat).toFixed(6)}, {Number(l.lng).toFixed(6)} • radius {l.radius_m} m •{" "}
                  {(l.employees || []).length ? `${l.employees.length} employee(s)` : "everyone"}
                </p>
              </div>
              <Button size="sm" variant="destructive" data-testid={`geofence-delete-${l.id}`}
                onClick={async () => {
                  if (!window.confirm(`Delete "${l.name}"?`)) return;
                  try {
                    await api.delete(`/tracking/locations/${l.id}`);
                    toast.success("Work location deleted");
                    reload();
                  } catch (e) {
                    toast.error(apiError(e));
                  }
                }}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TrackingSettings = ({ settings, reload }) => {
  const [form, setForm] = useState(settings);
  return (
    <div className="surface space-y-3 p-4" data-testid="tracking-settings">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Location update interval">
          <Select value={String(form.interval_seconds)}
            onValueChange={(v) => setForm({ ...form, interval_seconds: Number(v) })}>
            <SelectTrigger data-testid="tracking-interval"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(settings.interval_choices || [30, 60, 120, 300, 600]).map((s) => (
                <SelectItem key={s} value={String(s)}>
                  {s < 60 ? `${s} seconds` : `${s / 60} minute(s)`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Employees may start their own field tracking">
          <div className="pt-2">
            <Switch checked={form.employee_can_start} data-testid="tracking-employee-can-start"
              onCheckedChange={(employee_can_start) => setForm({ ...form, employee_can_start })} />
          </div>
        </Field>
        <Field label="Map provider">
          <Select value={form.map_provider} onValueChange={(map_provider) => setForm({ ...form, map_provider })}>
            <SelectTrigger data-testid="tracking-map-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="osm">OpenStreetMap (no key needed)</SelectItem>
              <SelectItem value="google">Google Maps (needs an API key)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Google Maps API key" className="sm:col-span-2">
          <Input placeholder={settings.google_maps_api_key === "set" ? "Key saved — type to replace" : "Paste your Google Maps JavaScript API key"}
            data-testid="tracking-google-key"
            onChange={(e) => setForm({ ...form, google_maps_api_key: e.target.value })} />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">
        Google Maps needs a key from Google Cloud Console (Maps JavaScript API). Until a key is
        saved the map uses OpenStreetMap. Tracking only runs inside an authorised session, and a
        phone browser cannot send location reliably while the app is closed — a native Android
        build is required for true background tracking.
      </p>
      <Button size="sm" className="gap-1.5" data-testid="tracking-settings-save"
        onClick={async () => {
          try {
            await api.put("/tracking/settings", {
              interval_seconds: Number(form.interval_seconds),
              employee_can_start: !!form.employee_can_start,
              geofence_required: !!form.geofence_required,
              map_provider: form.map_provider,
              google_maps_api_key: form.google_maps_api_key === "set" ? "" : form.google_maps_api_key || "",
            });
            toast.success("Tracking settings saved");
            reload();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}>
        <Save className="h-4 w-4" /> Save settings
      </Button>
    </div>
  );
};

export default function LiveTracking() {
  const [board, setBoard] = useState(null);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const [starting, setStarting] = useState("");
  const [historyFor, setHistoryFor] = useState("");
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    api.get("/tracking/live").then(({ data }) => setBoard(data)).catch((e) => logError("live board", e));
    api.get("/tracking/locations").then(({ data }) => setLocations(data)).catch((e) => logError("locations", e));
    api.get("/tracking/settings").then(({ data }) => setSettings(data)).catch((e) => logError("tracking settings", e));
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const rows = useMemo(() => board?.sessions || [], [board]);
  const trail = useMemo(() => (history?.pings || []).slice(0, 60).map((p) => ({ lat: p.lat, lng: p.lng })), [history]);

  const startFor = async (userId) => {
    setStarting(userId);
    try {
      await api.post("/tracking/sessions/start", { user_id: userId, purpose: "Field duty" });
      toast.success("Field tracking started — the employee's app will show TRACKING ACTIVE");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setStarting("");
    }
  };

  if (!board || !settings) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        testId="tracking-header"
        title="Live Field Tracking"
        subtitle={`${board.active} authorised session(s) • ${board.live_now} sending live updates • every ${Math.round(board.interval_seconds / 60)} min`}
      />
      <div className="space-y-4 p-4 sm:p-6">
        <Tabs defaultValue="live">
          <TabsList data-testid="tracking-tabs">
            <TabsTrigger value="live" data-testid="tab-live">Live map</TabsTrigger>
            <TabsTrigger value="start" data-testid="tab-start">Start / stop</TabsTrigger>
            <TabsTrigger value="geofences" data-testid="tab-geofences">Work locations</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-tracking-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="mt-4 space-y-3">
            <LiveMap rows={rows} locations={locations} trail={[]} />
            <div data-testid="tracking-live-list" className="space-y-2">
              {rows.length === 0 ? (
                <Empty title="No tracking session is running" hint="Start one from the Start / stop tab when an employee goes on field duty" />
              ) : (
                rows.map((r) => (
                  <div key={r.id} className="surface flex flex-wrap items-center justify-between gap-3 p-4"
                    data-testid={`tracking-row-${r.id}`}>
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Navigation className="h-4 w-4 text-primary" /> {r.user_name}
                        <span className="text-xs text-muted-foreground">{r.employee_code}</span>
                        {freshBadge(r)}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {r.status.toUpperCase()} • {r.purpose} • authorised by {r.authorised_by} •
                        last updated {minutesAgo(r.age_seconds)}
                        {r.last_location
                          ? ` • ${Number(r.last_location.lat).toFixed(5)}, ${Number(r.last_location.lng).toFixed(5)} (±${r.last_location.accuracy ?? "?"} m)`
                          : " • no position received yet"}
                        {r.last_geofence?.location_name ? ` • ${r.last_geofence.location_name}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`tracking-history-${r.id}`}
                        onClick={async () => {
                          setHistoryFor(r.user_id);
                          const { data } = await api.get(`/tracking/history/${r.user_id}`);
                          setHistory(data);
                          toast.success("History loaded — see the History tab");
                        }}>
                        History
                      </Button>
                      <Button size="sm" variant="destructive" className="gap-1.5"
                        data-testid={`tracking-stop-${r.id}`}
                        onClick={async () => {
                          try {
                            await api.post(`/tracking/sessions/${r.id}/stop`);
                            toast.success("Tracking ended");
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}>
                        <Square className="h-3.5 w-3.5" /> Stop
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="start" className="mt-4 space-y-2" data-testid="tracking-start-list">
            <p className="text-sm text-muted-foreground">
              Only Admin/HR can switch tracking on. It stops automatically when the employee checks out.
            </p>
            {employees.map((e) => {
              const running = rows.find((r) => r.user_id === e.id);
              return (
                <div key={e.id} className="surface flex items-center justify-between gap-3 p-3"
                  data-testid={`tracking-employee-row-${e.id}`}>
                  <div className="text-sm">
                    {e.name}
                    <span className="ml-2 text-xs text-muted-foreground">{e.role_label}</span>
                  </div>
                  {running ? (
                    <Badge variant="outline" className="text-[10px]">{running.label}</Badge>
                  ) : (
                    <Button size="sm" className="gap-1.5" disabled={starting === e.id}
                      data-testid={`tracking-start-${e.id}`} onClick={() => startFor(e.id)}>
                      {starting === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                      Start field tracking
                    </Button>
                  )}
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="geofences" className="mt-4">
            <Geofences locations={locations} employees={employees} reload={load} />
          </TabsContent>

          <TabsContent value="history" className="mt-4 space-y-3">
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Employee">
                <Select value={historyFor} onValueChange={async (v) => {
                  setHistoryFor(v);
                  const { data } = await api.get(`/tracking/history/${v}`);
                  setHistory(data);
                }}>
                  <SelectTrigger className="w-64" data-testid="history-employee-select">
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {history ? (
              <>
                <LiveMap rows={[]} locations={locations} trail={trail} />
                <div className="surface p-4" data-testid="history-events">
                  <div className="overline mb-1">Geofence events</div>
                  {history.events.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No entry/exit events recorded.</p>
                  ) : (
                    history.events.map((ev) => (
                      <div key={ev.id} className="border-t border-border py-1.5 text-xs">
                        {ev.location_name || "Work location"} — {ev.event} — {String(ev.at).slice(11, 16)}
                      </div>
                    ))
                  )}
                </div>
                <div className="surface p-4" data-testid="history-pings">
                  <div className="overline mb-1">{history.pings.length} location update(s)</div>
                  {history.pings.slice(0, 40).map((p) => (
                    <div key={p.id} className="border-t border-border py-1.5 text-xs">
                      {String(p.recorded_at).slice(0, 16).replace("T", " ")} •{" "}
                      <span className="font-mono-num">{Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)}</span>{" "}
                      • ±{p.accuracy ?? "?"} m • {p.geofence?.status === "verified" ? p.geofence.location_name : "outside geofence"}
                      {p.sync_state === "offline_pending_sync" && " • synced from offline"}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <Empty title="Pick an employee to see their tracked positions" />
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <TrackingSettings settings={settings} reload={load} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
